import api from './api';
import { resetFeatureStorageOnAuthChange } from './featureStorage';
import { getMarketingSiteOrigin } from '@/utils/hostOrigins';

export interface RegisterRequest {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
}

export interface RegisterInvitedTeacherRequest {
  email: string;
  password: string;
  invitationToken: string;
  firstName?: string;
  lastName?: string;
}

export interface RegisterStudentRequest {
  email: string;
  password: string;
  invitationToken: string;
  firstName?: string;
  lastName?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface AuthResponse {
  token: string;
  expiresAt: string;
  user: UserDto;
}

export interface InvitationResponse {
  invitationToken: string;
  email: string;
  expiresAt: string;
  invitationUrl: string;
}

export interface TeacherInvitationDto {
  id: string;
  tenantId: string;
  email: string;
  status: string;
  expiresAt: string;
  createdAt: string;
  invitationUrl: string;
}

export interface StudentInvitationResponse {
  invitationToken: string;
  email: string;
  expiresAt: string;
  invitationUrl: string;
}

export interface StudentInvitationDto {
  id: string;
  tenantId: string;
  email: string;
  status: string;
  expiresAt: string;
  createdAt: string;
  invitationUrl: string;
}

export interface InvitationValidationResponse {
  valid: boolean;
  email?: string;
  tenantName?: string;
  expiresAt?: string;
}

export interface UserDto {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  avatarUrl?: string | null;
  createdAt: string;
  role?: string; // 'teacher', 'student', 'superadmin'
  tenantId?: string | null; // For superadmin, this might be null
}

// Normalize user object coming from API/cookies (which may use PascalCase) into our UserDto shape
const normalizeUser = (raw: any): UserDto => {
  if (!raw) {
    throw new Error('Cannot normalize empty user');
  }

  const id = raw.id ?? raw.Id;
  const email = raw.email ?? raw.Email;
  const firstName = raw.firstName ?? raw.FirstName ?? null;
  const lastName = raw.lastName ?? raw.LastName ?? null;
  const avatarUrl = raw.avatarUrl ?? raw.AvatarUrl ?? null;
  const role = raw.role ?? raw.Role;
  const tenantId = raw.tenantId ?? raw.TenantId ?? null;
  const createdAt = raw.createdAt ?? raw.CreatedAt;

  return {
    id,
    email,
    firstName,
    lastName,
    avatarUrl,
    role,
    tenantId,
    createdAt,
  };
};

const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));
  if (!match) return null;
  const value = match.split('=')[1];
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

export const authService = {
  async register(data: RegisterRequest): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>('/auth/register', data);
    const normalizedUser = normalizeUser((response.data as any).user);
    resetFeatureStorageOnAuthChange();
    return {
      ...response.data,
      user: normalizedUser,
    };
  },

  async registerInvitedTeacher(data: RegisterInvitedTeacherRequest): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>('/auth/register-invited-teacher', data);
    const normalizedUser = normalizeUser((response.data as any).user);
    resetFeatureStorageOnAuthChange();
    return {
      ...response.data,
      user: normalizedUser,
    };
  },

  async registerStudent(data: RegisterStudentRequest): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>('/auth/register-student', data);
    const normalizedUser = normalizeUser((response.data as any).user);
    resetFeatureStorageOnAuthChange();
    return {
      ...response.data,
      user: normalizedUser,
    };
  },

  async login(data: LoginRequest): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>('/auth/login', data);
    const normalizedUser = normalizeUser((response.data as any).user);
    resetFeatureStorageOnAuthChange();
    return {
      ...response.data,
      user: normalizedUser,
    };
  },

  async requestPasswordReset(email: string): Promise<void> {
    await api.post('/auth/forgot-password', { email: email.trim() });
  },

  async completePasswordReset(accessToken: string, newPassword: string): Promise<void> {
    await api.post('/auth/reset-password', { accessToken, newPassword });
  },

  async getCurrentUser(): Promise<UserDto> {
    // For dashboard we rely entirely on the shared public cookie set by the API.
    // If it's not present or invalid, we treat the user as unauthenticated and
    // DO NOT call /auth/me (which currently returns 404 when no profile exists).
    try {
      const userCookie = getCookie('arrangelly_user');
      if (userCookie) {
        const parsedRaw = JSON.parse(userCookie);
        return normalizeUser(parsedRaw);
      }
      throw new Error('No dashboard auth cookie');
    } catch (e) {
      throw e;
    }
  },

  async logout() {
    // Tell API to clear all auth cookies (arrangelly_token, arrangelly_user, arrangelly_expires_at)
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Error during logout API call:', error);
    }
    resetFeatureStorageOnAuthChange();
    window.location.href = getMarketingSiteOrigin();
  },

  async inviteTeacher(tenantId: string, email: string): Promise<InvitationResponse> {
    const response = await api.post<InvitationResponse>('/auth/invite-teacher', { tenantId, email });
    return response.data;
  },

  async inviteStudent(email: string): Promise<StudentInvitationResponse> {
    const response = await api.post<StudentInvitationResponse>('/auth/invite', { email });
    return response.data;
  },

  async getStudentInvitations(): Promise<StudentInvitationDto[]> {
    const response = await api.get<StudentInvitationDto[]>('/auth/student-invitations');
    return Array.isArray(response.data) ? response.data : [];
  },

  async validateTeacherInvitationToken(token: string): Promise<InvitationValidationResponse> {
    const response = await api.get<InvitationValidationResponse>('/auth/validate-teacher-invitation', { params: { token } });
    return response.data;
  },

  async validateStudentInvitationToken(token: string): Promise<InvitationValidationResponse> {
    const response = await api.get<InvitationValidationResponse>('/auth/validate-invitation', { params: { token } });
    return response.data;
  },

  async getTeacherInvitations(tenantId: string): Promise<TeacherInvitationDto[]> {
    const response = await api.get<TeacherInvitationDto[]>(`/auth/teacher-invitations/${tenantId}`);
    return Array.isArray(response.data) ? response.data : [];
  },

  async updateProfile(firstName?: string, lastName?: string, avatarUrl?: string): Promise<UserDto> {
    const response = await api.put<UserDto>('/auth/me', { firstName, lastName, avatarUrl });
    return normalizeUser(response.data as any);
  },

  async uploadAvatar(file: File): Promise<UserDto> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post<UserDto>('/auth/me/avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return normalizeUser(response.data as any);
  },

  async resendTeacherInvitation(invitationId: string): Promise<InvitationResponse> {
    const response = await api.post<InvitationResponse>(`/auth/teacher-invitations/${invitationId}/resend`);
    return response.data;
  },

  async revokeTeacherInvitation(invitationId: string): Promise<void> {
    await api.post(`/auth/teacher-invitations/${invitationId}/revoke`);
  },

  getToken(): string | null {
    return getCookie('arrangelly_token');
  },

  getUser(): UserDto | null {
    try {
      const userCookie = getCookie('arrangelly_user');
      if (!userCookie) return null;
      const parsedRaw = JSON.parse(userCookie);
      const parsed = normalizeUser(parsedRaw);
      return parsed;
    } catch {
      return null;
    }
  },

  setAuth(_authResponse: AuthResponse) {
    // API already set the shared auth cookie (arrangelly_token); nothing to store on client
  },

  async switchTenant(_tenantId: string): Promise<void> {
    // Tenant selection is handled elsewhere; no storage here
  },

  getSelectedTenantId(): string | null {
    const sessionValue = sessionStorage.getItem('selectedTenantId');
    if (sessionValue) return sessionValue;
    return localStorage.getItem('selectedTenantId');
  },
};

